import { equationCatalog } from "./equationCatalog.js";
import { evidenceClasses, equationProvenance, provenanceByEquationId } from "./equationProvenance.js";

export const promotionRegistryVersion = "strategy-equation-promotion-0.1.0";

export const promotionGateStates = {
  PASSED: "PASSED",
  BLOCKED: "BLOCKED",
  MANUAL_REVIEW: "MANUAL_REVIEW"
};

export const promotionDecisions = {
  OPERATOR_MODEL_ONLY: "OPERATOR_MODEL_ONLY",
  READY_FOR_EXPERIMENT: "READY_FOR_EXPERIMENT",
  RESTRICTED_REVIEW_ONLY: "RESTRICTED_REVIEW_ONLY"
};

export function promotionAssessmentForEquation(equationId) {
  const equation = equationCatalog.find((entry) => entry.id === equationId);
  const provenance = provenanceByEquationId(equationId);
  if (!equation || !provenance) return null;

  const hasLocalCountableEvidence = provenance.evidenceClass === evidenceClasses.LOCAL_COUNTABLE_SPOT_CHECKED;
  const hasSpotChecks = provenance.spotCheckIds.length > 0;
  const hasLicensedOrReviewBlocker =
    provenance.evidenceClass === evidenceClasses.LICENSED_ANCHOR_ONLY ||
    provenance.evidenceClass === evidenceClasses.SOURCE_ANCHOR_ONLY ||
    provenance.evidenceClass === evidenceClasses.RECOVERED_OPERATOR_MODEL ||
    provenance.evidenceClass === evidenceClasses.MIXED_RESTRICTED;

  const gates = [
    {
      id: "property_tests",
      label: "Property tests declared",
      state: promotionGateStates.PASSED,
      evidence: "test/equations.test.mjs"
    },
    {
      id: "local_countable_custody",
      label: "Local countable custody",
      state: hasLocalCountableEvidence ? promotionGateStates.PASSED : promotionGateStates.BLOCKED,
      evidence: hasLocalCountableEvidence ? "corpus/public-domain/art-of-war-giles-1910/pg132.txt" : "source/custody review required"
    },
    {
      id: "spot_check_pack",
      label: "Manual spot-check pack",
      state: hasSpotChecks ? promotionGateStates.PASSED : promotionGateStates.BLOCKED,
      evidence: hasSpotChecks ? provenance.spotCheckIds.join(", ") : "no exact local spot checks attached"
    },
    {
      id: "empirical_validation",
      label: "Empirical validation",
      state: promotionGateStates.BLOCKED,
      evidence: "no reproducible outcome-study receipt exists"
    },
    {
      id: "safety_or_license_review",
      label: "Safety/license review",
      state: hasLicensedOrReviewBlocker ? promotionGateStates.MANUAL_REVIEW : promotionGateStates.PASSED,
      evidence: provenance.promotionBlockers.join("; ")
    }
  ];

  const blockedCount = gates.filter((gate) => gate.state === promotionGateStates.BLOCKED).length;
  const manualReviewCount = gates.filter((gate) => gate.state === promotionGateStates.MANUAL_REVIEW).length;
  const decision =
    equation.lifecycleState === "RESTRICTED" || manualReviewCount > 0
      ? promotionDecisions.RESTRICTED_REVIEW_ONLY
      : blockedCount > 0
        ? promotionDecisions.OPERATOR_MODEL_ONLY
        : promotionDecisions.READY_FOR_EXPERIMENT;

  return {
    equationId,
    lifecycleState: equation.lifecycleState,
    decision,
    promotionAllowed: false,
    formalVerificationClaim: false,
    blockedCount,
    manualReviewCount,
    gates,
    boundary:
      "Promotion gates manage operator-model lifecycle only. They do not grant formal proof, production prediction, or professional advice status."
  };
}

export const equationPromotionAssessments = equationProvenance.map((entry) =>
  promotionAssessmentForEquation(entry.equationId)
);

export function promotionSummary(assessments = equationPromotionAssessments) {
  return assessments.reduce(
    (summary, assessment) => {
      summary.total += 1;
      summary[assessment.decision] = (summary[assessment.decision] ?? 0) + 1;
      if (assessment.promotionAllowed) summary.promotionAllowed += 1;
      if (assessment.formalVerificationClaim) summary.formalVerificationClaims += 1;
      summary.blockedGates += assessment.blockedCount;
      summary.manualReviewGates += assessment.manualReviewCount;
      return summary;
    },
    { total: 0, promotionAllowed: 0, formalVerificationClaims: 0, blockedGates: 0, manualReviewGates: 0 }
  );
}
